import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined'
import { PageHeader } from '@/components/common/PageHeader'

interface ModulePlaceholderPageProps {
  /** Module title, e.g. "Audit Plan". */
  title: string
  /** One-line description of what the module will hold. */
  description?: string
}

/**
 * Placeholder for an approved sidebar module whose page is not built yet.
 *
 * Access is already real: the route is gated by the module's `<module>:view`
 * permission, so this only stands in for the screen itself. Replace the route
 * element with the real page when it is ready — the sidebar needs no change.
 */
export default function ModulePlaceholderPage({ title, description }: ModulePlaceholderPageProps) {
  return (
    <>
      <PageHeader title={title} subtitle={description} />
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 6 } }}>
        <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              color: 'text.secondary',
            }}
          >
            <ConstructionOutlinedIcon fontSize="large" />
          </Box>
          <Typography variant="h6">This module is under construction</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
            You can see this in the sidebar because your group grants the <strong>View</strong>{' '}
            permission for this module. The page itself is coming soon.
          </Typography>
          <Alert severity="info" sx={{ maxWidth: 460, textAlign: 'left' }}>
            Sidebar visibility and access are already permission-controlled. When the real page is
            added, no navigation or permission changes are needed.
          </Alert>
        </Stack>
      </Paper>
    </>
  )
}
